import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { GuestPurpose, GuestVisitStatus, PageRequest } from "./types";

export interface VisitFilter {
  /** Visits still going on or after this date. */
  fromDate?: string | undefined;
  /** Visits that start on or before this date. */
  toDate?: string | undefined;
  /** Only visits that start on or after this date. */
  arrivingFrom?: string | undefined;
  guestId?: string | undefined;
  hostMemberId?: string | undefined;
  /** Words of the guest's name, email or affiliation. */
  q?: string | undefined;
  /** Words of the host's name or member number. */
  host?: string | undefined;
  purpose?: readonly GuestPurpose[] | undefined;
  status?: readonly GuestVisitStatus[] | undefined;
  /** Latest first, for a guest's or a member's history. */
  newestFirst?: boolean | undefined;
}

function visitQuery(filter: VisitFilter) {
  return {
    from_date: filter.fromDate,
    to_date: filter.toDate,
    arriving_from: filter.arrivingFrom,
    guest_id: filter.guestId,
    host_member_id: filter.hostMemberId,
    q: filter.q,
    host: filter.host,
    purpose: filter.purpose as readonly string[] | undefined,
    status: filter.status as readonly string[] | undefined,
    newest_first: filter.newestFirst,
  };
}

/**
 * The guests module: guest records, visits, the club's guest policy and blackout dates.
 * Every method names its operation id, so a renamed route fails to compile after `npm run api:generate`.
 */
export function guestsEndpoints({ http, get, post, patch, del, paged }: Helpers) {
  return {
    // ---- guests
    listGuests: (
      page: PageRequest & { q?: string; hostMemberId?: string; visitFrom?: string; visitTo?: string; includeMerged?: boolean } = {},
    ) =>
      get<Out<"guests_list_guests">>(
        "/guests",
        paged(page, { q: page.q, host_member_id: page.hostMemberId, visit_from: page.visitFrom, visit_to: page.visitTo, include_merged: page.includeMerged }),
      ),
    getGuest: (id: string, signal?: AbortSignal) => get<Out<"guests_get_guest">>(`/guests/${id}`, { signal }),
    /** A guest with the same name and email or phone is refused with 409 `guest_duplicate` (matches in `details`) unless `allow_duplicate`. */
    createGuest: (input: In<"guests_create_guest">) => post<Out<"guests_create_guest">>("/guests", input),
    updateGuest: (id: string, input: In<"guests_update_guest">) => patch<Out<"guests_update_guest">>(`/guests/${id}`, input),
    addGuestHost: (id: string, memberId: string) => post<Out<"guests_add_guest_host">>(`/guests/${id}/hosts`, { member_id: memberId }),
    removeGuestHost: (id: string, memberId: string) => del<Out<"guests_remove_guest_host">>(`/guests/${id}/hosts/${memberId}`),
    guestDuplicates: (id: string, signal?: AbortSignal) => get<Out<"guests_guest_duplicates">>(`/guests/${id}/duplicates`, { signal }),
    /** Fold `sourceId` into guest `id`. */
    mergeGuest: (id: string, sourceId: string) => post<Out<"guests_merge_guest">>(`/guests/${id}/merge`, { source_id: sourceId }),

    // ---- visits
    /** `purpose` and `status` may list several values. */
    listVisits: (page: PageRequest & VisitFilter = {}) => get<Out<"guests_list_visits">>("/guest-visits", paged(page, visitQuery(page))),
    getVisit: (id: string, signal?: AbortSignal) => get<Out<"guests_get_visit">>(`/guest-visits/${id}`, { signal }),
    createVisit: (input: In<"guests_create_visit">) => post<Out<"guests_create_visit">>("/guest-visits", input),
    updateVisit: (id: string, input: In<"guests_update_visit">) => patch<Out<"guests_update_visit">>(`/guest-visits/${id}`, input),
    /** The guest policy's verdict on a visit, without making it. */
    checkVisit: (input: In<"guests_check_visit">, signal?: AbortSignal) =>
      http.request<Out<"guests_check_visit">>("POST", "/guest-visits/check", { body: input, signal }),
    checkIn: (id: string) => post<Out<"guests_check_in_visit">>(`/guest-visits/${id}/check-in`),
    checkOut: (id: string) => post<Out<"guests_check_out_visit">>(`/guest-visits/${id}/check-out`),
    noShow: (id: string) => post<Out<"guests_no_show_visit">>(`/guest-visits/${id}/no-show`),
    cancelVisit: (id: string, reason: string | null) => post<Out<"guests_cancel_visit">>(`/guest-visits/${id}/cancel`, { reason }),
    postFee: (id: string) => post<Out<"guests_post_visit_fee">>(`/guest-visits/${id}/fee/post`),
    waiveFee: (id: string, reason: string) => post<Out<"guests_waive_visit_fee">>(`/guest-visits/${id}/fee/waive`, { reason }),

    // ---- policy and blackout dates
    getPolicy: (signal?: AbortSignal) => get<Out<"guests_get_policy">>("/guest-policy", { signal }),
    updatePolicy: (input: In<"guests_update_policy">) => patch<Out<"guests_update_policy">>("/guest-policy", input),
    listBlackouts: (page: PageRequest & { fromDate?: string } = {}) =>
      get<Out<"guests_list_blackouts">>("/guest-blackouts", paged(page, { from_date: page.fromDate })),
    createBlackout: (input: In<"guests_create_blackout">) => post<Out<"guests_create_blackout">>("/guest-blackouts", input),
    updateBlackout: (id: string, input: In<"guests_update_blackout">) => patch<Out<"guests_update_blackout">>(`/guest-blackouts/${id}`, input),
    deleteBlackout: (id: string) => del<Out<"guests_delete_blackout">>(`/guest-blackouts/${id}`),
  };
}
